<script setup lang="ts">
import { computed } from 'vue'
import MarkdownIt from 'markdown-it'
import DOMPurify from 'isomorphic-dompurify'
import type { TNotificationFormat } from '../types/notification.types'

/**
 * Рендер текстового содержимого уведомления с учётом поля format.
 *
 * Безопасность:
 * - format=`html` — текст идёт через DOMPurify перед v-html (защита от XSS даже если бек
 *   когда-то начнёт пропускать пользовательский контент в уведомления).
 * - format=`markdown` — markdown-it → HTML → DOMPurify → v-html.
 * - format=`default` (или неизвестный) — plain text без рендера.
 */
const props = withDefaults(
  defineProps<{
    text?: string
    message?: string
    format?: TNotificationFormat
  }>(),
  {
    text: '',
    message: '',
    format: 'default',
  },
)

// Singleton markdown-парсера на компонент — лёгкий, можно создавать локально
const md = new MarkdownIt({
  html: false, // блокируем сырой HTML внутри markdown — DOMPurify ниже сделает дополнительную чистку
  linkify: true,
  breaks: true,
})

const contentText = computed(() => props.text || props.message || '')

const rendered = computed<string>(() => {
  const t = contentText.value
  if (!t) return ''
  if (props.format === 'html') {
    return DOMPurify.sanitize(t)
  }
  if (props.format === 'markdown') {
    return DOMPurify.sanitize(md.render(t))
  }

  return ''
})

const isRich = computed(() => props.format === 'html' || props.format === 'markdown')
</script>

<template>
  <div
    v-if="isRich"
    class="notification-content notification-content--rich"
    v-html="rendered"
  />
  <div
    v-else
    class="notification-content notification-content--plain"
  >
    {{ contentText }}
  </div>
</template>

<style lang="scss" scoped>
.notification-content {
  word-break: break-word;
  white-space: pre-line;

  &--rich {
    :deep(p) {
      margin: 0;
    }

    :deep(a) {
      color: rgb(var(--v-theme-primary));
      text-decoration: underline;
    }

    :deep(ul),
    :deep(ol) {
      padding-inline-start: 20px;
      margin-block: 4px;
    }

    :deep(code) {
      padding: 2px 4px;
      background: rgba(var(--v-theme-on-surface), 0.06);
      border-radius: 4px;
      font-size: 0.9em;
    }
  }
}
</style>
